import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

/* ---------------- TYPES ---------------- */

interface ContentProtection {
  screenshotAllowed: boolean;
  screenRecordingAllowed: boolean;
  screenShareAllowed: boolean;
}

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  portalType: string;
  role: string;
  roleName?: string;
  organizationId?: string;
  permissions?: string[];
  ip?: string;
  contentProtection?: ContentProtection;
}

interface AuthContextType {
  user: User | null;
  permissions: string[];           // Added: same as tech portal
  loading: boolean;
  connectionError: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

export type LoginResult =
  | { requiresPasswordChange: true; passwordChangeToken: string; email: string }
  | { requiresPasswordChange: false };

/* ---------------- CONTEXT ---------------- */

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? "" : "http://localhost:3000");

if (import.meta.env.DEV) {
  console.log("API_URL:", API_URL || "(empty - using Vite proxy)");
}

/* ---------------- PROVIDER ---------------- */

/* ---------------- TIMEOUT FETCH HELPER ---------------- */
const fetchWithTimeout = (url: string, options: RequestInit = {}, timeoutMs = 5000): Promise<Response> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timer));
};

type MeBootstrapResult = {
  user: User | null;
  permissions: string[];
  connectionError: boolean;
  aborted?: boolean;
};

/** Share one in-flight /auth/me across StrictMode remounts (avoids duplicate network calls). */
const meBootstrapInflight = new Map<string, Promise<MeBootstrapResult>>();

function meUrl(): string {
  if (import.meta.env.DEV && !import.meta.env.VITE_API_URL) {
    return "/api/v1/auth/me";
  }
  return `${API_URL}/api/v1/auth/me`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]); // Added
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const result = await checkAuth();
      if (cancelled || result?.aborted) return;
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for localStorage changes (cross-tab token sync)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'accessToken' && event.newValue) {
        // Token was updated in another tab, refresh user data
        void checkAuth();
        queryClient.invalidateQueries();
      } else if (event.key === 'accessToken' && !event.newValue) {
        // Token was removed in another tab, clear user
        cleanup();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  /* ---------------- REFRESH TOKEN ---------------- */

  const refreshToken = async (): Promise<boolean> => {
    try {
      const refreshTokenValue = localStorage.getItem("refreshToken");
      if (!refreshTokenValue) return false;

      let url: string;
      if (import.meta.env.DEV && !import.meta.env.VITE_API_URL) {
        url = "/api/v1/auth/refresh";
      } else {
        url = `${API_URL}/api/v1/auth/refresh`;
      }

      const response = await fetchWithTimeout(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: refreshTokenValue }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("accessToken", data.data.accessToken);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Token refresh error:", error);
      return false;
    }
  };

  /* ---------------- CHECK AUTH (/me) ---------------- */

  const applyMeResult = (result: MeBootstrapResult) => {
    if (result.aborted) return;
    setConnectionError(result.connectionError);
    // Keep existing session when the network check fails/times out
    if (!result.connectionError) {
      setUser(result.user);
      setPermissions(result.permissions);
    }
    setLoading(false);
  };

  const fetchMeBootstrap = async (token: string): Promise<MeBootstrapResult> => {
    const existing = meBootstrapInflight.get(token);
    if (existing) return existing;

    const promise = (async (): Promise<MeBootstrapResult> => {
      try {
        let response = await fetchWithTimeout(meUrl(), {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.status === 401) {
          const refreshed = await refreshToken();
          if (!refreshed) {
            cleanup();
            return { user: null, permissions: [], connectionError: false };
          }

          const newToken = localStorage.getItem("accessToken");
          if (!newToken) {
            cleanup();
            return { user: null, permissions: [], connectionError: false };
          }

          response = await fetchWithTimeout(meUrl(), {
            headers: { Authorization: `Bearer ${newToken}` },
          });
        }

        if (response.ok) {
          // Session cleared mid-flight (e.g. first-login password redirect)
          if (!localStorage.getItem("accessToken")) {
            return { user: null, permissions: [], connectionError: false, aborted: true };
          }
          const data = await response.json();
          return {
            user: data.data,
            permissions: data.data.permissions || [],
            connectionError: false,
          };
        }

        if (response.status === 401 || response.status === 403) {
          cleanup();
          return { user: null, permissions: [], connectionError: false };
        }

        return { user: null, permissions: [], connectionError: false };
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          console.warn('Auth check timeout - preserving session');
          return { user: null, permissions: [], connectionError: true };
        }
        if (error?.name === 'TypeError' && error?.message?.includes('Failed to fetch')) {
          console.warn('Auth check connection error - preserving session');
          return { user: null, permissions: [], connectionError: true };
        }
        console.error("Auth check error:", error);
        cleanup();
        return { user: null, permissions: [], connectionError: false };
      }
    })().finally(() => {
      meBootstrapInflight.delete(token);
    });

    meBootstrapInflight.set(token, promise);
    return promise;
  };

  const checkAuth = async (): Promise<MeBootstrapResult | undefined> => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        setConnectionError(false);
        setLoading(false);
        return { user: null, permissions: [], connectionError: false };
      }

      setConnectionError(false);
      const result = await fetchMeBootstrap(token);

      // Session cleared while request was in flight (e.g. first-login password flow)
      if (!localStorage.getItem("accessToken")) {
        setUser(null);
        setPermissions([]);
        setLoading(false);
        return { user: null, permissions: [], connectionError: false, aborted: true };
      }

      applyMeResult(result);
      return result;
    } catch (error) {
      console.error("Auth check error:", error);
      setLoading(false);
      return { user: null, permissions: [], connectionError: false };
    }
  };

  /* ---------------- LOGIN ---------------- */

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      let url: string;
      if (import.meta.env.DEV && !import.meta.env.VITE_API_URL) {
        url = "/api/v1/auth/login";
      } else {
        url = `${API_URL}/api/v1/auth/login`;
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password,
          portalType: "admin",
        }),
      });

      if (!response.ok) {
        let errorMessage = "Login failed";
        try {
          const error = await response.json();
          errorMessage = error.error || error.message || errorMessage;
        } catch {
          errorMessage = response.statusText || `HTTP ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!data.success || !data.data) {
        throw new Error("Invalid response from server");
      }

      const loginData = data.data;

      if (loginData.requiresPasswordChange) {
        if (!loginData.passwordChangeToken) {
          throw new Error("Password change required, but authorization was not issued. Please try again.");
        }
        // Clear leftover session so /auth/me and dashboard APIs cannot run
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        setUser(null);
        setPermissions([]);
        return {
          requiresPasswordChange: true,
          passwordChangeToken: loginData.passwordChangeToken,
          email: loginData.email || email,
        };
      }

      if (!loginData.accessToken || !loginData.refreshToken || !loginData.user) {
        throw new Error("Invalid response from server");
      }

      // Clear cache only for a successful full login
      queryClient.clear();

      localStorage.setItem("accessToken", loginData.accessToken);
      localStorage.setItem("refreshToken", loginData.refreshToken);

      setUser(loginData.user);
      setPermissions(loginData.permissions || loginData.user.permissions || []);
      setConnectionError(false);
      return { requiresPasswordChange: false };
    } catch (error: any) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error("Unable to connect to server. Please check if the API is running.");
      }
      throw error;
    }
  };

  /* ---------------- LOGOUT ---------------- */

  const logout = async () => {
    try {
      // Capture logout time so next login can show missed notifications
      try {
        const { recordAdminLogoutTimestamp } = await import(
          '../components/notifications/MissedNotificationsRail'
        );
        recordAdminLogoutTimestamp();
      } catch {
        localStorage.setItem('admin_last_logout_at', new Date().toISOString());
      }

      const token = localStorage.getItem("accessToken");
      const refreshTokenValue = localStorage.getItem("refreshToken");

      if (token && refreshTokenValue) {
        let url: string;
        if (import.meta.env.DEV && !import.meta.env.VITE_API_URL) {
          url = "/api/v1/auth/logout";
        } else {
          url = `${API_URL}/api/v1/auth/logout`;
        }

        await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refreshToken: refreshTokenValue }),
        });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      cleanup();
      navigate("/login");
    }
  };

  /* ---------------- CLEANUP ---------------- */

  const cleanup = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");

    // Clear user-specific data
    localStorage.removeItem("admin_rfq_draft");

    // Clear Query Cache
    queryClient.clear();

    setUser(null);
    setPermissions([]);
    setConnectionError(false);
  };

  /* ---------------- PROVIDER VALUE ---------------- */

  return (
    <AuthContext.Provider
      value={{
        user,
        permissions,           // Now exposed!
        loading,
        connectionError,
        login,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/* ---------------- HOOK ---------------- */

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
